import { DataIcon, DotsFilledIcon, GearIcon, HistoryIcon, PdfIcon, SaveIcon } from '@approvalmax/ui';
import { Button, Dropdown, Flex, Menu, toast } from '@approvalmax/ui/src/components';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { domain, State, stateTree } from 'modules/data';
import { FC, memo, useCallback, useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch, useSelector } from 'react-redux';
import { amplitudeService } from 'services/amplitude';
import { api } from 'services/api';
import { ConfirmationDropdown, FeatureBadge, HelpStripe, SubscribeAfterTrialButton } from 'shared/components';
import { SubscribeAfterTrialButtonType } from 'shared/components/SubscribeAfterTrialButton/SubscribeAfterTrialButton.types';
import { getCanShowSubscribeAfterTrialButton } from 'shared/helpers/experiments';

import {
    closeVersionHistoryPanel,
    deleteTemplate as actionDeleteTemplate,
    discardTemplateChanges as actionDiscardTemplateChanges,
    FETCH_WORKFLOW_WITH_VERSION,
    fetchVersionHistory,
    openCopyTemplatePopup,
    openFieldSettingsPopup,
    openSettingsPopup,
    openStartOverPopup,
    openVersionHistoryPanel,
    saveTemplate,
} from '../../../../actions';
import { useIsGetGroupOfValuesAvailable } from '../../../../hooks/useIsGetGroupOfValuesAvailable';
import { StartOverPopupMode } from '../../../../reducers/page/activeStartOverReducer';
import { AddCommentPopupPostAction } from '../../../../reducers/page/addCommentPopupReducer';
import { AllCommands, getAllCommands } from '../../../../selectors/cardCommandSelectors';
import { getActiveTemplate, getVersionHistory, isPageReadonly } from '../../../../selectors/pageSelectors';
import { getHasChanges } from '../../../../selectors/templateSelectors';
import { groupOfValuesPopupOpenState } from '../../../GroupOfValuesPopup/GroupOfValuesPopup.states';
import { WorkflowVersionHistoryPromotion } from '../WorkflowVersionHistoryPromotion/WorkflowVersionHistoryPromotion';
import { commandInfo, largeButtonCommandIds, menuCommandIds, saveButtonCommandIds } from './Actions.constants';
import { useShowBadges } from './Actions.hooks';
import { messages } from './Actions.messages';
import { ActionsProps, CommandDefinition } from './Actions.types';

const qa = bemFactory.qa('wfc-toolbar');

const Actions: FC<ActionsProps> = memo((props) => {
    const { helpStripeId } = props;

    const dispatch = useDispatch();

    const setGroupOfValuesPopupOpenState = useSetAtom(groupOfValuesPopupOpenState);
    const activeTemplate = useSelector(getActiveTemplate)!;
    const versionHistory = useSelector(getVersionHistory);
    const company = useSelector((state: State) => selectors.company.getCompanyById(state, activeTemplate.companyId));
    const commands = useSelector(getAllCommands);
    const readonly = useSelector(isPageReadonly);
    const isFetchingWorkflow = useSelector((state: stateTree.State) =>
        selectors.ui.isOperationInProgress(state, FETCH_WORKFLOW_WITH_VERSION)
    );
    const { showBetaBadge, showPremiumBadge } = useShowBadges();
    const isGetGroupOfValuesAvailable = useIsGetGroupOfValuesAvailable();

    const isWorkflowVersioningAvailable = company.licenseFeatures.includes(
        domain.CompanyLicenseFeature.WorkflowVersioning
    );

    const isShowWorkflowVersioningPromotion =
        !isWorkflowVersioningAvailable &&
        (company.licenseProductPlan === domain.CompanyPlan.Xero ||
            company.licenseProductPlan === domain.CompanyPlan.QBooks) &&
        company.allFeaturesTrialStatus === domain.AllFeaturesTrialStatus.AwaitingActivation;

    const isTemplateHasUnsavedChanges = useSelector(getHasChanges);

    const profile = useSelector(selectors.profile.findProfile);
    const canShowSubscribeAfterTrialButton = getCanShowSubscribeAfterTrialButton(company, profile);

    /**
     * @see Email Generator: workflowReport
     */
    const exportPdf = useCallback(() => {
        window.open(
            `${api.expandUrl('templates/export')}?templateId=${activeTemplate.id}&companyId=${company.id}`,
            '_blank'
        );
    }, [activeTemplate.id, company.id]);

    const openTemplateSettings = useCallback(() => {
        amplitudeService.sendData('workflow: open settings', {
            workflow: activeTemplate.integrationCode?.toLocaleLowerCase() || 'standalone',
        });

        if (activeTemplate.isNew) {
            (dispatch(saveTemplate(activeTemplate)) as any).then(() => {
                dispatch(openSettingsPopup());
            });
        } else {
            dispatch(openSettingsPopup());
        }
    }, [dispatch, activeTemplate]);

    const toggleVersionHistory = useCallback(() => {
        if (versionHistory.isOpen) {
            dispatch(closeVersionHistoryPanel());
        } else {
            dispatch(openVersionHistoryPanel());

            if (!versionHistory.templates.length) dispatch(fetchVersionHistory());
        }

        amplitudeService.sendData('workflow: open version history', {
            workflow: activeTemplate.integrationCode?.toLocaleLowerCase() || 'standalone',
        });
    }, [dispatch, activeTemplate.integrationCode, versionHistory.isOpen, versionHistory.templates.length]);

    const startOver = useCallback(() => {
        dispatch(openStartOverPopup(activeTemplate, StartOverPopupMode.Regular, company.flags.isWorkflowManager));
    }, [dispatch, activeTemplate, company.flags.isWorkflowManager]);

    const discardTemplateChanges = useCallback(() => {
        dispatch(actionDiscardTemplateChanges());
    }, [dispatch]);

    const disableTemplate = useCallback(() => {
        dispatch(
            saveTemplate(activeTemplate, {
                enabled: false,
                addCommentIfNeed: isWorkflowVersioningAvailable,
                addCommentPopupPostAction: AddCommentPopupPostAction.Deactivate,
            })
        );
    }, [activeTemplate, dispatch, isWorkflowVersioningAvailable]);

    const deleteTemplate = useCallback(() => {
        dispatch(actionDeleteTemplate());
    }, [dispatch]);

    const saveDraftTemplate = useCallback(() => {
        dispatch(
            saveTemplate(activeTemplate, {
                addCommentIfNeed: isWorkflowVersioningAvailable,
                addCommentPopupPostAction: AddCommentPopupPostAction.Save,
            })
        );
    }, [isWorkflowVersioningAvailable, activeTemplate, dispatch]);

    const createTemplate = useCallback(() => {
        dispatch(saveTemplate(activeTemplate));
    }, [dispatch, activeTemplate]);

    const updateEnabledTemplate = useCallback(() => {
        dispatch(
            saveTemplate(activeTemplate, {
                addCommentIfNeed: isWorkflowVersioningAvailable,
                addCommentPopupPostAction: AddCommentPopupPostAction.Update,
            })
        );
    }, [isWorkflowVersioningAvailable, activeTemplate, dispatch]);

    const copyTemplate = useCallback(() => {
        if (isTemplateHasUnsavedChanges) {
            toast.error(messages.copyTemplateUnsavedError);

            return;
        }

        dispatch(openCopyTemplatePopup(activeTemplate));
    }, [dispatch, activeTemplate, isTemplateHasUnsavedChanges]);

    const enableTemplate = useCallback(() => {
        dispatch(
            saveTemplate(activeTemplate, {
                enabled: true,
                addCommentIfNeed: isWorkflowVersioningAvailable,
                addCommentPopupPostAction: AddCommentPopupPostAction.Activate,
            })
        );
    }, [activeTemplate, isWorkflowVersioningAvailable, dispatch]);

    const openFieldSettings = useCallback(() => {
        dispatch(openFieldSettingsPopup());
    }, [dispatch]);

    const openGroupOfValuesPopup = useCallback(() => {
        setGroupOfValuesPopupOpenState(true);
    }, [setGroupOfValuesPopupOpenState]);

    const methods: { [key in keyof AllCommands]?: () => void } = useMemo(
        () => ({
            startOver,
            enableTemplate,
            updateEnabledTemplate,
            createTemplate,
            saveDraftTemplate,
            disableTemplate,
            discardTemplateChanges,
            copyTemplate,
            deleteTemplate,
        }),
        [
            copyTemplate,
            createTemplate,
            deleteTemplate,
            disableTemplate,
            discardTemplateChanges,
            enableTemplate,
            saveDraftTemplate,
            startOver,
            updateEnabledTemplate,
        ]
    );

    const getFilteredCommands = useCallback(
        (commandIds: Array<keyof AllCommands>): CommandDefinition[] => {
            // UGLY: should be refactored
            return commandIds
                .map((id: keyof AllCommands) => ({
                    id,
                    execute: methods[id],
                    ...commands[id],
                    ...commandInfo[id],
                }))
                .filter((cmd) => !cmd.hidden);
        },
        [commands, methods]
    );

    const constMenuCommandItems = useMemo(
        () =>
            getFilteredCommands(menuCommandIds).map((command) => ({
                id: command.id,
                name: command.text || '',
                onClick: command.execute,
                disabled: command.disabled || readonly,
                title: command.title,
                'data-qa': qa(`${command.id}-menu-item`),
                confirmation: command.confirmation,
            })),
        [getFilteredCommands, readonly]
    );

    const saveButtonCommand = useMemo(() => getFilteredCommands(saveButtonCommandIds)[0], [getFilteredCommands]);
    const largeButtonCommand = useMemo(() => getFilteredCommands(largeButtonCommandIds)[0], [getFilteredCommands]);

    return (
        <Flex spacing='12' alignItems='center' justifyContent='end' shrink={0}>
            {showPremiumBadge && <FeatureBadge premium />}

            {showBetaBadge && <FeatureBadge beta />}

            <HelpStripe.ToggleButton id={helpStripeId} />

            {isGetGroupOfValuesAvailable && (
                <Button
                    title={messages.groupOfValues}
                    onClick={openGroupOfValuesPopup}
                    disabled={isFetchingWorkflow || company.isReadonly}
                    icon
                    noPadding
                    data-qa={qa('group-of-values-button')}
                    variant='text'
                    color='blue100'
                >
                    <DataIcon size={24} />
                </Button>
            )}

            {activeTemplate.documentFields &&
                activeTemplate.documentFields.length > 0 &&
                !commands.updateFieldSettings.hidden && (
                    <Button
                        title={messages.fieldSettingsButtonTitle}
                        onClick={openFieldSettings}
                        disabled={isFetchingWorkflow || commands.updateFieldSettings.disabled}
                        icon
                        noPadding
                        data-qa={qa('fieldSettings-button')}
                        variant='text'
                        color='blue100'
                    >
                        <DataIcon size={24} />
                    </Button>
                )}

            {!commands.exportPdf.hidden && (
                <Button
                    title={messages.exportPDF}
                    onClick={exportPdf}
                    disabled={!activeTemplate.isActual || isFetchingWorkflow || commands.exportPdf.disabled}
                    icon
                    noPadding
                    data-qa={qa(`export-pdf-button`)}
                    variant='text'
                    color='blue100'
                >
                    <PdfIcon size={24} />
                </Button>
            )}

            {!commands.openTemplateSettings.hidden && (
                <Button
                    title={commandInfo.openTemplateSettings.title}
                    onClick={openTemplateSettings}
                    disabled={isFetchingWorkflow || commands.openTemplateSettings.disabled || activeTemplate.isNew}
                    icon
                    noPadding
                    variant='text'
                    color='blue100'
                >
                    <GearIcon size={24} />
                </Button>
            )}

            {isWorkflowVersioningAvailable && (
                <Button
                    icon
                    noPadding
                    noUppercase
                    variant='text'
                    color='blue100'
                    title={messages.versions}
                    onClick={toggleVersionHistory}
                    disabled={activeTemplate.version === undefined || isFetchingWorkflow}
                >
                    <HistoryIcon size={24} />
                </Button>
            )}

            {isShowWorkflowVersioningPromotion && <WorkflowVersionHistoryPromotion company={company} />}

            {saveButtonCommand && !saveButtonCommand.hidden && (
                <Button
                    title={saveButtonCommand.title}
                    onClick={saveButtonCommand.execute}
                    disabled={readonly || isFetchingWorkflow || saveButtonCommand.disabled}
                    icon
                    noPadding
                    data-qa={qa(`${saveButtonCommand.id}-button`)}
                    variant='text'
                    color='blue100'
                >
                    <SaveIcon size={24} />
                </Button>
            )}

            <Dropdown
                activator={
                    <Button icon noPadding data-qa={qa('more-actions-button')} variant='text' color='blue100'>
                        <DotsFilledIcon />
                    </Button>
                }
                width='auto'
            >
                <Menu>
                    {constMenuCommandItems.map((item) => {
                        if (item.confirmation && item.onClick) {
                            const { onClick, confirmation, ...rest } = item;

                            return (
                                <ConfirmationDropdown
                                    key={item.id}
                                    title={confirmation}
                                    display='block'
                                    onConfirm={onClick}
                                >
                                    <Menu.Item {...rest} />
                                </ConfirmationDropdown>
                            );
                        }

                        return <Menu.Item key={item.id} {...item} />;
                    })}
                </Menu>
            </Dropdown>

            {largeButtonCommand &&
                !largeButtonCommand.hidden &&
                (readonly && canShowSubscribeAfterTrialButton && profile ? (
                    <SubscribeAfterTrialButton
                        profileId={profile.id}
                        companyId={company.id}
                        text={largeButtonCommand.text}
                        type={SubscribeAfterTrialButtonType.WorkflowActivation}
                    />
                ) : (
                    <Button
                        title={largeButtonCommand.title}
                        onClick={largeButtonCommand.execute}
                        disabled={readonly || isFetchingWorkflow || largeButtonCommand.disabled}
                        color='green80'
                        size='small'
                        data-qa={qa(`${largeButtonCommand.id}-button`)}
                        data-starter-guide={
                            largeButtonCommand.id === 'enableTemplate' ? 'wf_set_up_activate_the_wf' : undefined
                        }
                    >
                        {largeButtonCommand.text}
                    </Button>
                ))}
        </Flex>
    );
});

export default Actions;
